import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { openCamera, openPhotoPicker } from "@/lib/camera";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Receipt, Upload, Check, Pencil, Trash2, Loader2, X, DollarSign, Mic, MicOff
} from "lucide-react";
import { toast } from "sonner";
import { useAI } from "@/lib/ai";
import { AIResultNote } from "@/components/AIResultNote";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function VoiceInput({ onTranscript, disabled }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const startListening = () => {
    if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
      toast.error("Voice input not supported in this browser");
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      onTranscript(transcript);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
  };

  return (
    <Button variant="outline" size="sm" onClick={listening ? stopListening : startListening}
      disabled={disabled}
      className={`text-xs ${listening ? "bg-red-50 border-red-300 text-red-700" : ""}`}
      data-testid="receipt-voice-input">
      {listening ? <><MicOff className="w-3.5 h-3.5 mr-1" /> Stop</> : <><Mic className="w-3.5 h-3.5 mr-1" /> Voice</>}
    </Button>
  );
}

function ReceiptCard({ receipt, onApprove, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(receipt.description || "");
  const [amount, setAmount] = useState(receipt.amount || 0);
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await onApprove(receipt.id, desc, amount);
      setEditing(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border border-zinc-200 overflow-hidden" data-testid={`receipt-card-${receipt.id}`}>
      <div className="relative">
        <img
          src={`${API_BASE}/api/photos/receipts/${receipt.id}/image`}
          alt="Receipt"
          className="w-full h-36 object-cover bg-zinc-100"
          loading="lazy"
        />
        <button
          onClick={() => onDelete(receipt.id)}
          className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
          data-testid={`delete-receipt-${receipt.id}`}
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>
        {receipt.is_approved && (
          <Badge className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px]">
            <Check className="w-3 h-3 mr-0.5" /> Approved
          </Badge>
        )}
        {receipt.amount > 0 && (
          <Badge className="absolute bottom-2 left-2 bg-zinc-900/80 text-white text-xs">
            <DollarSign className="w-3 h-3 mr-0.5" />{receipt.amount.toFixed(2)}
          </Badge>
        )}
      </div>
      <CardContent className="p-3 space-y-2">
        {!editing ? (
          <>
            <div>
              <p className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold mb-0.5">
                {receipt.ai_description ? "AI Analysis" : "Description"}
              </p>
              <p className="text-xs text-zinc-700 leading-relaxed">{receipt.description}</p>
              {receipt.ai_description && <AIResultNote feature="receipt" refId={receipt.id} text={receipt.ai_description} />}
            </div>
            {receipt.amount > 0 && (
              <div className="flex items-center gap-1 text-sm font-semibold text-zinc-900">
                <DollarSign className="w-3.5 h-3.5" /> {receipt.amount.toFixed(2)}
              </div>
            )}
            {!receipt.is_approved && (
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={handleApprove} disabled={submitting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                  data-testid={`approve-receipt-${receipt.id}`}>
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Approve</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}
                  className="flex-1 text-xs h-8" data-testid={`edit-receipt-${receipt.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-zinc-500 font-bold">Edit Receipt</p>
              <VoiceInput onTranscript={(text) => setDesc(text)} disabled={submitting} />
            </div>
            <Textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2}
              className="text-xs" placeholder="Describe receipt items..."
              data-testid={`receipt-desc-input-${receipt.id}`} />
            <div>
              <Label className="text-xs text-zinc-500">Amount ($)</Label>
              <Input type="number" step="0.01" value={amount}
                onChange={e => setAmount(parseFloat(e.target.value) || 0)}
                className="mt-1 text-xs" placeholder="0.00"
                data-testid={`receipt-amount-input-${receipt.id}`} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleApprove} disabled={submitting}
                className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white text-xs h-8"
                data-testid={`save-receipt-${receipt.id}`}>
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save & Approve"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}
                className="text-xs h-8">Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function JobReceipts({ requestId }) {
  const { aiOn } = useAI();
  const [receipts, setReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchReceipts = async () => {
    try {
      const { data } = await API.get(`/photos/receipts/job/${requestId}`);
      setReceipts(Array.isArray(data?.receipts) ? data.receipts : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (requestId) fetchReceipts();
  }, [requestId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!requestId) {
      toast.error("Job context missing — cannot upload receipt");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Receipt image is too large (max 10MB). Please retake at lower quality.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("request_id", requestId);

    try {
      await API.post("/photos/receipts/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      toast.success("Receipt uploaded");
      fetchReceipts();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to upload receipt";
      toast.error(msg);
      console.error("Receipt upload failed:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const handleApprove = async (receiptId, description, amount) => {
    try {
      await API.post(`/photos/receipts/${receiptId}/approve`, { description, amount });
      toast.success("Receipt approved");
      fetchReceipts();
    } catch (err) {
      toast.error("Failed to approve");
    }
  };

  const handleDelete = async (receiptId) => {
    try {
      await API.delete(`/photos/receipts/${receiptId}`);
      toast.success("Receipt deleted");
      fetchReceipts();
    } catch (err) {
      toast.error("Failed to delete receipt");
    }
  };

  const totalAmount = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="space-y-3" data-testid="job-receipts-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-zinc-400" />
          <h4 className="text-sm font-heading font-semibold text-zinc-900">
            Receipts ({receipts.length})
          </h4>
          {totalAmount > 0 && (
            <Badge variant="secondary" className="text-xs">
              <DollarSign className="w-3 h-3 mr-0.5" />{totalAmount.toFixed(2)} total
            </Badge>
          )}
        </div>
      </div>

      {/* Upload buttons */}
      <div className="flex gap-2">
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
          className="hidden" onChange={handleUpload} data-testid="receipt-camera-input" />
        <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif"
          className="hidden" onChange={handleUpload} data-testid="receipt-file-input" />
        <Button size="sm" variant="outline" onClick={() => openCamera(cameraInputRef)}
          disabled={uploading} className="flex-1 text-xs h-9" data-testid="take-receipt-photo-button">
          {uploading ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {aiOn ? "AI Analyzing..." : "Uploading..."}</>
          ) : (
            <><Receipt className="w-3.5 h-3.5 mr-1.5" /> Snap Receipt</>
          )}
        </Button>
        <Button size="sm" variant="outline" onClick={() => openPhotoPicker(fileInputRef)}
          disabled={uploading} className="flex-1 text-xs h-9" data-testid="upload-receipt-button">
          <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload
        </Button>
      </div>

      {/* Receipt grid */}
      {receipts.length > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {receipts.map(r => (
            <ReceiptCard key={r.id} receipt={r}
              onApprove={handleApprove} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {receipts.length === 0 && !uploading && (
        <div className="text-center py-4 border border-dashed border-zinc-200 rounded-xl">
          <Receipt className="w-6 h-6 text-zinc-300 mx-auto mb-1.5" />
          <p className="text-xs text-zinc-400">No receipts yet. Upload receipt photos for materials & parts.</p>
          {aiOn && <p className="text-[10px] text-zinc-400 mt-0.5">AI will extract items and amounts automatically</p>}
        </div>
      )}
    </div>
  );
}
